/**
 * EventDetailsCard.js — Goosecross detailed event card.
 *
 * OVERRIDES: src/components/base/EventDetailsCard.js
 * REASON: Goose detail view starts with a "banner curve" top strip (a
 *   decorative SVG/PNG divider) above the cover image, uses a custom SVG icon
 *   prefix for the price, and ends with a reservation button that has a
 *   calendar-with-check icon.
 *
 * INTERFACE: render(ev) → HTMLElement, identical signature to base.
 *
 * Reference: goosecross/script.js Lines 2117-2152 (createEventDetails).
 */

const PRICE_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M5 6h14v3H5zm0 5h14v8H5zm2 2v4h10v-4z" opacity=".85"/></svg>';

const RESERVE_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8"  y1="2" x2="8"  y2="6"/><line x1="3"  y1="10" x2="21" y2="10"/><polyline points="9 15 11 17 15 13"/></svg>';

export default class EventDetailsCard {
  render(ev) {
    const card = document.createElement("div");
    card.className = "ws-card ws-event-details-card ws-rich-event-details";

    // Top-design strip — purely decorative; styled in variables.css scope.
    const strip = document.createElement("div");
    strip.className = "ws-rich-event-strip";
    card.appendChild(strip);

    if (ev.cover_image_link) {
      const head = document.createElement("div");
      head.className = "ws-card-head";
      const img = document.createElement("img");
      img.className = "ws-card-img";
      img.src = ev.cover_image_link;
      img.alt = ev.title || "Event";
      img.loading = "lazy";
      head.appendChild(img);
      card.appendChild(head);
    }

    const body = document.createElement("div");
    body.className = "ws-card-body";

    if (ev.date) {
      const d = document.createElement("p");
      d.className = "ws-card-meta ws-rich-event-date";
      d.textContent = ev.date;
      body.appendChild(d);
    }

    if (ev.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title";
      title.title = ev.title;
      title.textContent = ev.title;
      body.appendChild(title);
    }

    const hr = document.createElement("div");
    hr.className = "ws-card-divider";
    body.appendChild(hr);

    const paragraphs = Array.isArray(ev.description)
      ? ev.description
      : ev.description
        ? [ev.description]
        : [];
    paragraphs.forEach((text) => {
      const p = document.createElement("p");
      p.className = "ws-card-desc-full";
      p.textContent = text;
      body.appendChild(p);
    });

    [ev.time, ev.location].filter(Boolean).forEach((text) => {
      const p = document.createElement("p");
      p.className = "ws-card-meta";
      p.textContent = text;
      body.appendChild(p);
    });

    const priceText = [ev.price, ev.price_unit].filter(Boolean).join(" ");
    if (priceText) {
      const priceRow = document.createElement("p");
      priceRow.className = "ws-card-price ws-rich-event-price";
      priceRow.innerHTML = `<span class="ws-goose-price-icon">${PRICE_ICON}</span><span>${priceText.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]))}</span>`;
      body.appendChild(priceRow);
    }

    if (ev.cta_link) {
      const cta = document.createElement("a");
      cta.className = "ws-card-cta ws-rich-reserve-cta";
      cta.href = ev.cta_link;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
      cta.innerHTML = `<span class="ws-goose-reserve-icon">${RESERVE_ICON}</span><span>${(ev.cta_text || "Reserve your spot").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]))}</span>`;
      body.appendChild(cta);
    }

    card.appendChild(body);
    return card;
  }
}
